import { FC, useEffect } from 'react';
// 2026-05-26 — Trocado de BrowserRouter pra HashRouter.
//
// Bug raiz: WGT Tizen Consumer (UN50U8800F com cert efflux) carrega via
// file:///opt/usr/apps/CN6b5CzWCS/res/wgt/index.html. Quando o React
// Router navega pra rota como '/stream', BrowserRouter chama
// history.pushState() que muda window.location.href pra file:///stream.
// Próximo window.location.reload() (ex: app:reload do admin) tenta
// recarregar file:///stream → ERR_ACCESS_DENIED + tela branca.
//
// HashRouter usa file:///index.html#/stream — o # nunca muda a URL real,
// só hash fragment. reload() sempre carrega index.html, e o app
// re-rota internamente. Funciona em file://, http://localhost (Capacitor
// Android) E https:// (browser comum) sem regressão.
//
// Vide debug em #40.
import { HashRouter } from 'react-router-dom';
import { observer } from 'mobx-react';
import ThemeProvider from '~/theme/provider';
import { StoreProvider, stores } from '~/stores';
import { kioskStore } from '~/stores/kiosk.store';
import { PlatformProvider, usePlatform } from '~/services/platform';
import { ScaledViewport } from '~/components/ScaledViewport';
import { useTVRemote } from '~/utils/hooks/useTVRemote';
import { useSyncInit } from '~/utils/hooks/useSyncInit';
import { useOrientationSync } from '~/utils/hooks/useOrientationSync';
import { useRemoteOpsInit } from '~/utils/hooks/useRemoteOpsInit';
import { useLogRocket } from '~/utils/hooks/useLogRocket';
import { usePlatformInit } from '~/utils/hooks/usePlatformInit';
import { useEmbeddedChannel } from '~/utils/hooks/useEmbeddedChannel';
import { RollbackService } from '~/services/ota/RollbackService';
import OtaService from '~/services/ota/OtaService';
import { otaLog } from '~/services/ota/OtaDebugLogger';
import remoteLogger from '~/services/logging/RemoteLogger';
import {
  installMemoryGuard,
  uninstallMemoryGuard,
} from '~/services/logging/memoryGuard';
import { analyticsService } from '~/services/analytics';
import UpdateIndicator from '~/components/UpdateIndicator';
import { BatteryMonitor } from '~/components/BatteryMonitor/BatteryMonitor';
import { ConfigManager } from '~/components/ConfigManager/ConfigManager';
import { KioskGuard } from '~/components';
// SPIKE THROWAWAY (VITE_AVPLAY_BACKEND, default OFF) — Fase 0 do plano AVPlay: valida o
// gate S2 (máscara DOM sobre o vídeo AVPlay). Flag OFF = comportamento de hoje.
import { AvplaySpike } from '~/scenes/Stream/AvplaySpike';
import Routes from './routes';
import { hostApiUrl } from '~/services/embedded/hostConfig';

const AppContent: FC = observer(() => {
  const platform = usePlatform();

  // 🆕 Expose platform service globally for sync.store to access during auto-registration
  useEffect(() => {
    const w = window as unknown as { __effluxPlatform?: unknown };
    w.__effluxPlatform = platform;
    // 2026-05-31 (v1.0.104) — Propaga pra observable do kioskStore. Antes o
    // getter isLocked lia de window.__effluxPlatform (não-observable) e o
    // KioskGuard ficava com isLocked=false stale no boot. Vide kiosk.store.ts.
    kioskStore.setPlatform(platform.platform);
    return () => {
      delete w.__effluxPlatform;
    };
  }, [platform]);

  // KIOSK_PASSWORD_PLAN Fase 3 / Nível 1: ativa bridge JS↔Java pro plugin nativo
  // KioskMode (Android) consumir volume/back keys enquanto o kiosk está locked.
  // No-op em outras plataformas (browser, Tizen, webOS, iOS).
  useEffect(() => {
    kioskStore.initNativeBridge();
    return () => {
      kioskStore.disposeNativeBridge();
    };
  }, []);

  usePlatformInit(); // Initialize deviceId early (for NoContent/MediaInfo display)
  useTVRemote();
  useSyncInit(); // Initialize sync with Content API
  useOrientationSync(); // Propaga orientação física → servidor em QUALQUER tela
  useRemoteOpsInit(); // Initialize Visualização Interativa WS client
  useLogRocket(); // Initialize LogRocket session recording
  useEmbeddedChannel(); // Canal host↔Efflux — NO-OP fora do alvo 'embedded' (gated)

  // Initialize remote logging (TV diagnostics)
  useEffect(() => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') return;

    const remoteLoggingEnabled = import.meta.env.VITE_REMOTE_LOGGING === 'true';
    if (!remoteLoggingEnabled) {
      return;
    }

    const apiUrl =
      import.meta.env.VITE_REMOTE_LOGGING_URL ||
      hostApiUrl() ||
      import.meta.env.VITE_API_URL ||
      '';
    const deviceId =
      stores.authStore.deviceId ||
      localStorage.getItem('efflux_device_id') ||
      'unknown';
    const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';

    remoteLogger.init({
      enabled: true,
      apiUrl,
      deviceId,
      platform: platform.platform,
      appVersion,
    });

    return () => {
      remoteLogger.stop();
    };
  }, [platform.platform, stores.authStore.deviceId]);

  // Initialize rollback service (skip on browser — no OTA to rollback)
  useEffect(() => {
    if (platform.platform === 'browser') return;
    const rollbackService = new RollbackService(platform);

    rollbackService.init().then((result) => {
      if (result.rolledBack) {
        console.warn('[App] Update rolled back', {
          previousVersion: result.previousVersion,
          reason: result.reason,
        });
      }
    });

    return () => rollbackService.destroy();
  }, [platform]);

  // Initialize OTA service
  //
  // 2026-05-24 — BUG CRÍTICO consertado: o deps array antes era só
  // [platform], MAS o effect lê `stores.authStore.deviceId`. No mount,
  // deviceId costuma ser null (auth está inicializando), o guard cai no
  // early return, e como deviceId não está nas deps, o effect NUNCA
  // re-roda quando auth completa.
  //
  // Sintoma em campo: Tab A7 ficava órfão de OTA — nunca chamava
  // /api/ota/check, então rollouts ficavam com 0/0 targeted. Força
  // Update do admin retornava { dispatched: 0 } pq o device não tinha
  // entry em tab_ota_device_updates.
  //
  // Fix: adicionar deviceId nas deps. authStore.deviceId é observable
  // (MobX), AppContent é wrapped em observer, então a mudança dispara
  // re-render + re-execução do effect.
  //
  // Re-run safety: o cleanup (return) chama otaService.stopChecking(),
  // garantindo que rodadas anteriores (mesmo a falha do mount com
  // deviceId=null) param antes da próxima rodada criar nova instância.
  // Telemetria de crash: reporta pro servidor os crashes capturados no boot
  // anterior (silent OOM / erro JS). Gated em deviceId (auth resolvida);
  // reportPendingCrashes tem guard próprio pra id temporário e é idempotente
  // (marca reportado). Delay pra sair do caminho crítico do boot — zero peso
  // no hot path (só roda 1× quando houve crash).
  useEffect(() => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') return;
    if (!stores.authStore.deviceId) return;
    const t = setTimeout(() => {
      void analyticsService.reportPendingCrashes();
    }, 8000);
    return () => clearTimeout(t);
  }, [stores.authStore.deviceId]);

  useEffect(() => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') return;

    // v1.0.77: log nativo em cada step do init pra debug em campo
    otaLog.info('App.tsx OTA useEffect — entered', {
      demoMode: import.meta.env.VITE_DEMO_MODE,
      otaEnabled: import.meta.env.VITE_OTA_ENABLED,
      platform: platform.platform,
    });

    // Check if OTA is enabled via environment variable
    const otaEnabled = import.meta.env.VITE_OTA_ENABLED === 'true';
    if (!otaEnabled) {
      // `info`: OTA desligado é CONFIGURAÇÃO do build web (que atualiza recarregando a
      // página), não anomalia. Como `warn` saía com stack trace no console a cada boot.
      otaLog.info('OTA disabled via VITE_OTA_ENABLED — abortando init');
      console.log('[App] OTA disabled via VITE_OTA_ENABLED');
      return;
    }

    const apiUrl = hostApiUrl() ?? import.meta.env.VITE_CONTENT_API_URL;
    const currentVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
    const deviceId = stores.authStore.deviceId;

    otaLog.info('OTA init env values', {
      apiUrl,
      currentVersion,
      deviceId: deviceId ?? 'null',
    });

    if (!apiUrl || !deviceId) {
      otaLog.warn(
        'OTA aguardando deviceId/apiUrl — useEffect vai re-rodar quando deviceId vier',
        {
          apiUrl: apiUrl ?? 'null',
          deviceId: deviceId ?? 'null',
        },
      );
      console.warn(
        '[App] OTA aguardando deviceId/apiUrl (will retry when auth completes)',
        { apiUrl, deviceId },
      );
      return;
    }

    // v1.0.73 — Polling agressivo (60s) no Android.
    //
    // Causa: provamos via /proc/net/tcp6 que o EventSource do WebView Capacitor
    // não mantém conexão TCP estável com api.efflux.media — SSE morre silencioso
    // sem dispatch de 'error', readyState fica OPEN mas TCP fechado. Comandos
    // push do admin (force-update via cmd:ota:check) nunca chegam no player.
    // Único caminho de descoberta de update sobra = polling natural do OtaService.
    //
    // Default era 30min com SSE "ativo" (5min se "drop"). Como o SSE zumbi não
    // dispara o "drop", ficava 30min sempre. Pra 10K tablets em campo isso
    // significa update latency até 30min após release publicada — operacional
    // ruim.
    //
    // Trade-off: 60s × 10K tablets = ~167 req/s no /api/ota/check (resposta
    // típica 200 bytes, GET cacheable). Validado com Renato em 2026-05-29.
    //
    // Plano futuro (Onda 2): migrar SSE pra plugin nativo (OkHttp +
    // EventSourceListener Java) — conexão sobrevive ao GC do WebView. Depois
    // disso, voltar polling pra 30min com SSE como caminho real-time.
    const ANDROID_POLLING_MS = 60_000;
    const checkIntervalMs =
      platform.platform === 'android' ? ANDROID_POLLING_MS : undefined;

    const otaService = new OtaService({
      apiUrl,
      deviceId,
      currentVersion,
      platform: platform.platform,
      checkIntervalMs,
    });

    otaService.setPlatformService(platform);
    stores.otaStore.init(otaService);

    // Browser não instala OTA (matriz de plataformas: "OTA install = N/A —
    // recarrega URL"; o web player atualiza recarregando a página). Mantém o
    // init do store (preserva currentVersion no overlay de debug) mas NÃO faz
    // polling de /api/ota/check — inútil no web. Espelha o gating do
    // RollbackService acima. stopChecking() no cleanup é no-op se nunca iniciou.
    // "Deixa pronto": quando um build novo chegar (web deploy / OTA na frota), o
    // gating já vem embutido — sem release dedicada só pra isso.
    const otaShouldPoll = platform.platform !== 'browser';
    if (otaShouldPoll) {
      otaService.startChecking();
    }

    otaLog.info('OTA service initialized', {
      currentVersion,
      platform: platform.platform,
      deviceId,
      checkIntervalMs: checkIntervalMs ?? 'default-30min',
      startChecking: otaShouldPoll,
    });
    console.log('[App] OTA service initialized', {
      currentVersion,
      platform: platform.platform,
      deviceId,
      checkIntervalMs: checkIntervalMs ?? 'default (30min)',
    });

    return () => {
      otaService.stopChecking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stores ref é estável
  }, [platform, stores.authStore.deviceId]);

  // PROACTIVE MEMORY-GUARD (2026-07-20) — reseta o app ANTES do OS OOM-matar
  // o WebView. Em TV consumer (Tizen) a memória renderer/nativa vaza em
  // playback contínuo e a OS mata o processo após ~2-4h — sem auto-relaunch,
  // a TV cai pra outro canal. O reset periódico (via platform.restart('refresh'))
  // libera a memória acumulada; como re-inicia o boot, o kiosk re-trava
  // sozinho (nada a fazer aqui). Anti-loop via boot-grace + min-interval
  // persistidos em localStorage. Config/defaults em memoryGuard.ts.
  useEffect(() => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') return;
    installMemoryGuard(platform, analyticsService);
    return () => uninstallMemoryGuard();
  }, [platform]);

  // Global error handler to prevent app crashes on TV
  useEffect(() => {
    const handleError = (event: ErrorEvent): void => {
      console.error('[App] Uncaught error:', event.error);
      event.preventDefault(); // Prevent app crash
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
      console.error('[App] Unhandled promise rejection:', event.reason);
      event.preventDefault(); // Prevent app crash
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
    };
  }, []);

  return (
    <KioskGuard>
      {import.meta.env.VITE_AVPLAY_SPIKE === 'true' && <AvplaySpike />}
      <ScaledViewport>
        <Routes />
        <BatteryMonitor />
        <UpdateIndicator />
        <ConfigManager />
      </ScaledViewport>
    </KioskGuard>
  );
});

const App: FC = () => {
  return (
    <PlatformProvider>
      <StoreProvider value={stores}>
        <ThemeProvider>
          <HashRouter>
            <AppContent />
          </HashRouter>
        </ThemeProvider>
      </StoreProvider>
    </PlatformProvider>
  );
};

export default App;
